import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import Navbar from '@/components/feature/Navbar';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function BlogPreviewPage() {
  const { getPreview } = useBlogPosts();
  const post = getPreview();

  useEffect(() => {
    document.title = post ? `Preview: ${post.title} | AK Energy Blog` : 'Preview | AK Energy Blog';
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-background-50 pt-32 text-center">
        <Navbar />
        <div className="max-w-xl mx-auto px-6">
          <i className="ri-eye-off-line text-5xl text-foreground-300 mb-4 block" />
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">No Preview Available</h1>
          <p className="text-foreground-600 mb-6">
            Click "Preview" from the blog editor to see how your post will look when published.
          </p>
          <Link
            to="/blog/admin"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line" /> Go to Blog Admin
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Preview Banner */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-yellow-400 text-yellow-900 py-2 px-4 text-center text-sm font-medium flex items-center justify-center gap-4">
        <span className="flex items-center gap-2">
          <i className="ri-eye-line" />
          This is a preview — only visible to you.
        </span>
        <Link
          to={`/blog/admin/edit/${post.slug}`}
          className="inline-flex items-center gap-1.5 px-3 py-1 bg-yellow-600 text-white rounded-md text-xs font-semibold hover:bg-yellow-700 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" /> Back to Edit
        </Link>
      </div>

      <main className="pt-32 md:pt-36">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              {post.tags.map((tag) => (
                <span key={tag} className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {formatDate(post.publishedAt)}
              </span>
            </div>
          </div>

          {/* Cover Image */}
          <div className="rounded-2xl overflow-hidden mb-10">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-auto max-h-[500px] object-cover"
            />
          </div>

          {/* Content */}
          <div
            className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-950 prose-p:text-foreground-700 prose-a:text-primary-600 prose-strong:text-foreground-900 prose-blockquote:border-l-4 prose-blockquote:border-secondary-300 prose-blockquote:bg-secondary-50/50 prose-blockquote:py-2 prose-blockquote:pl-4 prose-blockquote:rounded-r prose-ul:list-disc prose-ul:pl-5 prose-ol:list-decimal prose-ol:pl-5 mb-16"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>
      </main>
    </div>
  );
}